import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { formatPayRangeAmount } from "@components/utils/formatPayRangeAmount";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

dayjs.extend(advancedFormat);

export function formatAnchorAmount(value) {
  return formatPayRangeAmount(value);
}

export function formatCompactAmount(value) {
  return formatPayRangeAmount(value);
}

export function resolveFlagSrc(flag) {
  if (!flag) return "";
  if (/^https?:\/\//i.test(flag) || flag.includes("/")) {
    return flag;
  }
  return `https://flagcdn.com/${flag.toLowerCase()}.svg`;
}

const META_COUNTRY_CODES = {
  usa: "us",
  us: "us",
  "united states": "us",
  uk: "gb",
  "united kingdom": "gb",
  canada: "ca",
};

function emojiFlagToCountryCode(emoji) {
  if (!emoji) return "";
  return [...emoji]
    .map((char) => String.fromCharCode(char.codePointAt(0) - 0x1f1e6 + 97))
    .join("");
}

export function resolveFlagFromMeta(meta) {
  if (!meta) return "";

  const emojiMatch = meta.match(/^([\u{1F1E6}-\u{1F1FF}]{2})/u);
  if (emojiMatch) {
    return emojiFlagToCountryCode(emojiMatch[1]);
  }

  const leading = previewMetaWithoutLeadingFlag(meta)
    .split(/[\u00b7·-]/)[0]
    ?.trim()
    .toLowerCase();
  if (!leading) return "";

  return META_COUNTRY_CODES[leading] || (leading.length === 2 ? leading : "");
}

export function previewMetaWithoutLeadingFlag(meta) {
  if (!meta) return "";
  return meta
    .replace(/^[\u{1F1E6}-\u{1F1FF}]{2}\s*/u, "")
    .replace(/^\s+/, "");
}

export function isEffectiveDateWithinSubscription(effectiveDate, subscription) {
  if (!effectiveDate || !subscription?.effectiveFrom) return false;

  const date = dayjs(effectiveDate);
  const from = dayjs(subscription.effectiveFrom);

  if (date.isBefore(from, "day")) return false;

  if (subscription.effectiveTo) {
    const to = dayjs(subscription.effectiveTo);
    if (date.isAfter(to, "day")) return false;
  }

  return true;
}

function toPublishedVersionRange(from, to) {
  if (!from) return null;
  return { effectiveFrom: from, effectiveTo: to || null };
}

/**
 * Published version windows for the selected pay range (Rule 6).
 * Prefers an explicit `versions` list when the API sends one; otherwise
 * falls back to the list/detail `compensation_snapshot` window.
 */
export function extractPublishedVersionRanges(apiRow = {}) {
  const versions = apiRow.versions || apiRow.pay_range_versions;
  if (Array.isArray(versions) && versions.length) {
    return versions
      .map((version) =>
        toPublishedVersionRange(
          version.effective_from || version.effectiveFrom,
          version.effective_to ?? version.effectiveTo
        )
      )
      .filter(Boolean);
  }

  const snapshot = apiRow.compensation_snapshot;
  if (!snapshot || snapshot.type === "draft") return [];
  const range = toPublishedVersionRange(snapshot.effective_from, snapshot.effective_to);
  return range ? [range] : [];
}

/** True when `date` falls in at least one published version window. No ranges → unconstrained. */
export function isDateCoveredByPublishedVersions(date, ranges) {
  if (!date) return false;
  if (!ranges?.length) return true;

  return ranges.some((range) => {
    if (!range?.effectiveFrom) return false;
    if (date < range.effectiveFrom) return false;
    if (range.effectiveTo && date > range.effectiveTo) return false;
    return true;
  });
}

/** Native date-input bounds: earliest `effectiveFrom`, latest `effectiveTo` (null if any window is open-ended). */
export function getPublishedVersionDateBounds(ranges) {
  if (!ranges?.length) return { minDate: null, maxDate: null };

  const fromDates = ranges.map((range) => range.effectiveFrom).filter(Boolean).sort();
  const toDates = ranges.map((range) => range.effectiveTo);
  const allClosed = toDates.every(Boolean);

  return {
    minDate: fromDates[0] || null,
    maxDate: allClosed ? [...toDates].sort().at(-1) : null,
  };
}

function toDateKey(value) {
  if (value == null || value === "") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}

/**
 * When the selected pay range changes, keep the current date if it is still
 * covered; otherwise snap to today (if covered) or the next published start
 * — `max(today, version.effective_from)` for future-only coverage.
 */
export function defaultEffectiveDateForPublishedVersions(
  ranges,
  currentDate,
  todayDate = dayjs()
) {
  const today = toDateKey(todayDate);
  const current = toDateKey(currentDate);

  if (!ranges?.length) return current || today;

  if (current && isDateCoveredByPublishedVersions(current, ranges)) return current;
  if (today && isDateCoveredByPublishedVersions(today, ranges)) return today;

  const starts = ranges.map((range) => range.effectiveFrom).filter(Boolean).sort();
  const nextStart = starts.find((from) => today && from >= today) || starts[0];
  if (nextStart && isDateCoveredByPublishedVersions(nextStart, ranges)) return nextStart;

  const { maxDate } = getPublishedVersionDateBounds(ranges);
  if (maxDate && isDateCoveredByPublishedVersions(maxDate, ranges)) return maxDate;

  return current || today;
}

export function getTruncationDate(effectiveDate) {
  return dayjs(effectiveDate).subtract(1, "day");
}

export function buildPositionSubtitle(position) {
  if (!position) return "";
  const parts = [position.code, position.name, position.department].filter(Boolean);
  return parts.join(" · ");
}

export function buildConflictCopy(subscription, effectiveDate) {
  if (!subscription || !effectiveDate) {
    return { title: "", body: "" };
  }

  const rangeLabel = [subscription.payRangeCode, subscription.payRangeName]
    .filter(Boolean)
    .join(" · ");
  const truncationDate = formatFairPayDate(
    getTruncationDate(effectiveDate).format("YYYY-MM-DD")
  );
  const throughLabel = subscription.effectiveTo
    ? `through ${formatFairPayDate(subscription.effectiveTo)}`
    : "with no end date";

  return {
    title: "This will end an existing subscription",
    body: `This position is currently subscribed to ${rangeLabel} ${throughLabel}. Subscribing here will end that subscription on ${truncationDate} — the day before this one starts.`,
  };
}
